import { Shadow, Text, Skeleton, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box, Pressable } from 'native-base';
import { Popper } from 'native-base/lib/module/components/composites/Popper';
import React, { useRef } from 'react';
import { StyleSheet, View } from 'react-native';

import { cohortCellHeight } from '../../constants';

export namespace CohortCellTypes {
  export type Props = {
    value?: number;
    minValue?: number;
    maxValue?: number;
    isLoading?: boolean;
    monthCycle?: number;
    monthNameFull?: string;
    withTooltip?: boolean;
  };
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    height: cohortCellHeight,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const interpolateColor = (
  colorA: string, // Hex color of minValue
  colorB: string, // Hex color of maxValue
  value: number, // The value to map to the gradient
  minValue: number,
  maxValue: number
): string => {
  // Ensure value is within the bounds
  const clampedValue = Math.max(minValue, Math.min(value, maxValue));

  // Calculate ratio of the value within the range
  const ratio = (clampedValue - minValue) / (maxValue - minValue);

  // Function to convert hex to RGB
  const hexToRgb = (hex: string) => {
    const bigint = parseInt(hex.replace('#', ''), 16);
    return {
      r: (bigint >> 16) & 255,
      g: (bigint >> 8) & 255,
      b: bigint & 255,
    };
  };

  // Function to convert RGB to hex
  const rgbToHex = (r: number, g: number, b: number) => {
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
  };

  // Convert both colors to RGB
  const rgbA = hexToRgb(colorA);
  const rgbB = hexToRgb(colorB);

  // Interpolate between the two colors
  const r = Math.round(rgbA.r + ratio * (rgbB.r - rgbA.r));
  const g = Math.round(rgbA.g + ratio * (rgbB.g - rgbA.g));
  const b = Math.round(rgbA.b + ratio * (rgbB.b - rgbA.b));

  // Return the interpolated color as a hex
  return rgbToHex(r, g, b);
};

const maxValueColor = Color['brand.tertiary.principal'];
const minValueColor = Color['brand.primary.lightest'];

export const CohortCell: React.FC<CohortCellTypes.Props> = ({
  maxValue,
  minValue,
  value,
  isLoading,
  monthNameFull,
  monthCycle,
  withTooltip = true,
}) => {
  const [overlayVisible, setOverlayVisible] = React.useState(false);
  const ref = useRef<typeof Pressable>(null);
  const valueFormatted = Utils.Mask.Percentage(value);

  const handleHoverIn = () => {
    setOverlayVisible(true);
  };

  const handleHoverOut = () => {
    setOverlayVisible(false);
  };

  if (isLoading || value === undefined || maxValue === undefined || minValue === undefined) {
    return (
      <View style={styles.container}>
        <Skeleton width="100%" borderRadius="sm" height="inline.xxxs" />
      </View>
    );
  }

  const color = interpolateColor(minValueColor, maxValueColor, value, minValue, maxValue);
  const fontColor = value > maxValue / 2 ? 'neutral.white' : 'neutral.darkest';

  return (
    <Box style={[styles.container, { backgroundColor: color }]}>
      <Pressable
        flex={1}
        width="100%"
        alignItems="center"
        justifyContent="center"
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        ref={ref}>
        <Text color={fontColor}>{valueFormatted}</Text>
      </Pressable>
      {withTooltip && (
        <Popper triggerRef={ref} placement="top center">
          <Popper.Content isOpen={overlayVisible}>
            {overlayVisible && (
              <Shadow
                level={2}
                margin={Spacing['inline.xxxs']}
                padding={Spacing['inline.xxxs']}
                borderRadius={BoxRadius.sm}>
                <VStack space={Spacing['stack.nano']}>
                  <Text>{monthNameFull}</Text>
                  <Text variant="bold">Ciclo: {monthCycle}</Text>
                  <HStack space={Spacing['stack.nano']}>
                    <HStack space={Spacing['stack.quarck']} alignItems="center">
                      <Box
                        width="8px"
                        height="8px"
                        borderRadius={BoxRadius.circular}
                        backgroundColor={color}
                      />
                      <Text variant="bold">%conversão:</Text>
                    </HStack>
                    <Text variant="bold">{valueFormatted}</Text>
                  </HStack>
                </VStack>
              </Shadow>
            )}
          </Popper.Content>
        </Popper>
      )}
    </Box>
  );
};
